'use client'

import { ArrowUpRight, Calendar, ChevronRight, CreditCard, FileText, Truck } from "lucide-react"
import { resolveColorHex, getVendorPrimaryColor, formatDateLong } from "@/utils/util"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"

const PAGES = [
  { key: 'delivery', href: '/delivery', label: 'Хүргэлт', icon: Truck },
  { key: 'payment', href: '/payment', label: 'Төлбөр', icon: CreditCard },
  { key: 'service', href: '/service', label: 'Үйлчилгээ', icon: FileText },
] as const

type DocPageProps = {
  vendor?: any
  pageKey: 'delivery' | 'payment' | 'service'
  eyebrow: string
  titleLead: string
  titleAccent: string
  description: string
  htmlContent?: string | null
  emptyMessage: string
}

export default function DocPage({ vendor, pageKey, eyebrow, titleLead, titleAccent, description, htmlContent, emptyMessage }: DocPageProps) {
  const pathname = usePathname()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const createdAt = vendor?.createdAt ? formatDateLong(vendor.createdAt) : null

  return (
    <div className="bg-white">
      <div className="mx-auto w-full max-w-320 px-4 lg:px-8 py-8">
        <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">{titleLead} {titleAccent}</span>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* LEFT: editorial hero */}
          <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <div className="text-xs font-medium text-muted-foreground">{eyebrow}</div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight leading-snug text-foreground">
              {titleLead} {titleAccent}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{description}</p>

            {createdAt && (
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />Дэлгүүр нээгдсэн огноо: {createdAt}
              </div>
            )}
          </div>

          {/* RIGHT: prose + cross-links */}
          <div className="lg:col-span-8">
            {htmlContent ? (
              <article
                className="prose max-w-none text-foreground prose-headings:tracking-[-0.01em] prose-h2:text-[28px] lg:prose-h2:text-[32px] prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-3 prose-h2:pb-3 prose-h2:border-b prose-h2:border-border prose-p:text-[15px] prose-p:leading-[1.75] prose-a:underline prose-a:underline-offset-[3px] prose-li:my-1"
                style={{ ['--tw-prose-links' as any]: primary }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{emptyMessage}</p>
              </div>
            )}

            <div className="mt-12 border-t border-border pt-6">
              <div className="text-sm font-medium text-foreground mb-3">Бусад хуудас</div>
              <nav className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PAGES.map((p) => {
                  const active = p.key === pageKey
                  const Icon = p.icon
                  return (
                    <a
                      key={p.key}
                      href={navigateUrl(p.href, pathname)}
                      className={
                        'group text-left rounded-xl border p-4 transition-colors ' +
                        (active ? 'border-transparent text-white' : 'border-border bg-white hover:bg-muted/40')
                      }
                      style={active ? { background: primary } : undefined}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={'h-9 w-9 rounded-lg grid place-items-center shrink-0 ' + (active ? 'bg-white/20' : '')}
                          style={!active ? { background: `color-mix(in oklch, ${primary} 10%, white)`, color: primary } : undefined}
                        >
                          <Icon className="h-4.5 w-4.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium leading-tight">{p.label}</div>
                        </div>
                        <ArrowUpRight className={'h-4 w-4 shrink-0 ' + (active ? 'text-white' : 'text-muted-foreground group-hover:text-foreground')} />
                      </div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
